/**
 * Sichtbare Rueckmeldung fuer fehlgeschlagene Warenkorb-Aktionen.
 *
 * Alle Warenkorb-Bausteine des Themes melden Fehler ueber das Standardereignis
 * shopify:cart:error. Im Warenkorb hoerte darauf bisher niemand — nur die
 * Geschenkkarten-Komponente und die Sticky-Leiste auf der Produktseite. Eine
 * abgelehnte Mengenaenderung, ein fehlgeschlagenes Loeschen oder ein
 * Netzwerkabbruch landeten damit ausschliesslich in der Browser-Konsole: der
 * Kunde sah eine Menge, die es im Warenkorb gar nicht gab, ohne jeden Hinweis.
 *
 * Der Hinweis haengt bewusst an document.body und nicht im Warenkorb-Markup.
 * Nach einem Fehler wird der Abschnitt neu gerendert, damit die Anzeige wieder
 * dem echten Warenkorb entspricht — ein Element innerhalb des Abschnitts wuerde
 * dabei im selben Moment wieder ueberschrieben.
 *
 * Eigenes Stylesheet statt einer Regel in den Liquid-Dateien: die Klassennamen
 * kommen sonst nirgends vor, und in diesem Theme sind schon mehrere Overrides
 * still von spaeteren Regeln gleicher Spezifitaet ueberstimmt worden.
 */

const HIDE_AFTER_MS = 8000;

/**
 * Texte aus den Sprachdateien (WAR-006, 12.10.2026). snippets/scripts.liquid
 * legt sie in Theme.translations ab; Theme ist dort eine globale const und von
 * diesem Modul aus nur ueber den Namen erreichbar, nicht ueber window. Der
 * deutsche Rueckfall greift nur, wenn das Skript ohne scripts.liquid laeuft.
 */
function fuText(key, fallback) {
  try {
    // eslint-disable-next-line no-undef
    const t = typeof Theme !== 'undefined' && Theme.translations ? Theme.translations[key] : '';
    return t || fallback;
  } catch (e) {
    return fallback;
  }
}

const STYLES = `
.fu-cart-error {
  position: fixed;
  inset-block-start: 16px;
  inset-inline: 16px;
  /* Der Browser setzt fuer [popover] inset: 0 und margin: auto. Ohne diese
     beiden Zeilen zieht sich der Hinweis ueber die ganze Fensterhoehe. */
  inset-block-end: auto;
  margin: 0 auto;
  padding: 12px 14px;
  /* Der z-index zaehlt nur im Rueckfall ohne Popover-Unterstuetzung. */
  z-index: 2147483000;
  max-width: 420px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  border-radius: 10px;
  border: 1px solid #f0c9c8;
  background: #fdf3f2;
  color: #7a1f1e;
  font-size: 14px;
  line-height: 1.4;
  box-shadow: 0 6px 24px rgb(18 45 42 / 0.16);
  opacity: 0;
  transform: translateY(-8px);
  transition: opacity .2s ease, transform .2s ease;
}

.fu-cart-error.is-visible {
  opacity: 1;
  transform: translateY(0);
}

.fu-cart-error__icon {
  flex: none;
  width: 18px;
  height: 18px;
  margin-block-start: 1px;
  border-radius: 999px;
  background: #c2302f;
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  line-height: 18px;
  text-align: center;
}

.fu-cart-error__text {
  flex: 1 1 auto;
  min-width: 0;
}

.fu-cart-error__close {
  flex: none;
  width: 28px;
  height: 28px;
  margin: -4px -4px 0 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: inherit;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
}

.fu-cart-error__close:hover {
  background: rgb(122 31 30 / 0.08);
}

@media (prefers-reduced-motion: reduce) {
  .fu-cart-error {
    transition: none;
  }
}
`;

let styleInjected = false;
/** @type {HTMLElement | null} */
let banner = null;
/** @type {ReturnType<typeof setTimeout> | undefined} */
let hideTimer;

function injectStyles() {
  if (styleInjected) return;
  const style = document.createElement('style');
  style.textContent = STYLES;
  document.head.appendChild(style);
  styleInjected = true;
}

function hide() {
  clearTimeout(hideTimer);
  if (!banner) return;
  banner.classList.remove('is-visible');
  const el = banner;
  setTimeout(() => {
    try {
      el.hidePopover();
    } catch {
      // war nie ein Popover, oder schon geschlossen
    }
    el.remove();
  }, 250);
  banner = null;
}

/**
 * @param {string} message
 */
function show(message) {
  injectStyles();
  hide();

  const el = document.createElement('div');
  el.className = 'fu-cart-error';
  // role=alert liest Screenreader sofort vor, ohne den Fokus zu verschieben.
  el.setAttribute('role', 'alert');

  const icon = document.createElement('span');
  icon.className = 'fu-cart-error__icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.textContent = '!';

  const text = document.createElement('span');
  text.className = 'fu-cart-error__text';
  text.textContent = message;

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'fu-cart-error__close';
  close.setAttribute('aria-label', fuText('fu_hinweis_schliessen', 'Hinweis schließen'));
  close.textContent = '×';
  close.addEventListener('click', hide);

  el.append(icon, text, close);

  // Der Warenkorb-Drawer ist ein <dialog>, auf dem Handy per showModal()
  // geoeffnet. Der liegt damit in der Top-Layer des Browsers, und dorthin
  // reicht kein z-index — der Hinweis verschwand hinter dem Drawer.
  // Ein Popover landet ebenfalls in der Top-Layer, und zwar oberhalb von
  // allem, was vorher dorthin befoerdert wurde. Anders als showModal()
  // sperrt es die Bedienung dahinter nicht.
  el.setAttribute('popover', 'manual');
  document.body.appendChild(el);
  banner = el;

  try {
    el.showPopover();
  } catch {
    // Aeltere Browser ohne Popover-Unterstuetzung: in den offenen Dialog
    // haengen, damit der Hinweis wenigstens dort nicht verdeckt wird.
    el.removeAttribute('popover');
    const openDialog = document.querySelector('dialog[open]');
    if (openDialog) openDialog.appendChild(el);
  }

  // Ein Frame warten, sonst ueberspringt der Browser den Uebergang.
  requestAnimationFrame(() => el.classList.add('is-visible'));

  hideTimer = setTimeout(hide, HIDE_AFTER_MS);
}

const FALLBACK = fuText(
  'fu_warenkorb_fehler',
  'Der Warenkorb konnte nicht aktualisiert werden. Bitte versuche es noch einmal.'
);

/**
 * Erkennt Texte, die aus dem Maschinenraum stammen statt an den Kunden
 * gerichtet zu sein.
 *
 * Noetig, weil die Warenkorb-Bausteine des Themes im Fehlerfall schlicht
 * error.message weiterreichen. Das ist mal eine uebersetzte Shopify-Meldung
 * ("Aufgrund der Verfuegbarkeit wurde nur 1 Artikel hinzugefuegt."), mal der
 * Wortlaut einer JavaScript-Ausnahme. Solange niemand zuhoerte, fiel das nicht
 * auf — seit es einen sichtbaren Hinweis gibt, landet es vor dem Kunden.
 *
 * Beobachtet: "Unexpected token '<', "<!DOCTYPE "... is not valid JSON",
 * wenn eine Antwort HTML statt JSON enthaelt.
 *
 * @param {string} text
 * @returns {boolean}
 */
function looksTechnical(text) {
  return /JSON|DOCTYPE|Unexpected token|SyntaxError|TypeError|ReferenceError|NetworkError|Failed to fetch|AbortError|\bundefined\b|\bnull\b|<[a-z!/]/i.test(text);
}

/**
 * Englische Huellen ohne Aussage. /cart/add.js legt in error nur "Cart Error"
 * ab; der brauchbare Satz steht daneben in description. Solange niemand die
 * Ereignisse anzeigte, fiel das nicht auf.
 */
const SENTINELS = new Set([
  'cart error',
  'cart update failed',
  'failed to update cart',
  'add to cart failed',
  'invalid discount code',
  'error',
]);

/**
 * @param {unknown} value
 * @returns {string | null} Der Text, wenn er einem Kunden zumutbar ist.
 */
function usable(value) {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (!text) return null;
  if (SENTINELS.has(text.toLowerCase())) return null;
  if (looksTechnical(text)) return null;
  return text;
}

/**
 * Holt einen lesbaren Text aus dem Ereignis. Shopify liefert je nach Endpunkt
 * einen String, ein Objekt mit Feldfehlern oder gar nichts Brauchbares.
 *
 * @param {any} event
 * @returns {string}
 */
function messageFrom(event) {
  // description zuerst: dort steht bei /cart/add.js der aussagekraeftige Satz,
  // waehrend error nur die Huelle traegt.
  const direct = usable(event?.detail?.description) ?? usable(event?.error);
  if (direct) return direct;

  const errors = event?.detail?.errors;
  if (typeof errors === 'string') {
    const single = usable(errors);
    if (single) return single;
  } else if (errors && typeof errors === 'object') {
    const parts = Object.values(errors)
      .flat()
      .map(usable)
      .filter(Boolean);
    if (parts.length) return parts.join(' ');
  }

  return FALLBACK;
}

/**
 * Bausteine mit eigener Fehleranzeige. Das Rabattfeld schreibt seine Meldung
 * schon neben das Eingabefeld — und feuert daneben ein Ereignis mit einem
 * hartkodierten englischen Text. Ohne diese Ausnahme staende beides gleichzeitig
 * auf dem Bildschirm, einmal deutsch und einmal englisch.
 */
const SELF_REPORTING = 'cart-discount-component, cart-note, gift-card-recipient-form-component';

document.addEventListener('shopify:cart:error', (event) => {
  const target = event.target;
  if (target instanceof Element && target.closest(SELF_REPORTING)) return;

  show(messageFrom(event));
});

// Kein Ausblenden bei shopify:cart:lines-update: dieses Ereignis wird bereits
// VOR dem Request ausgeloest, auch bei dem Versuch, der gleich scheitert. Ein
// Hinweis wuerde dadurch im selben Moment wieder verschwinden, in dem er
// erscheint. Er geht stattdessen nach acht Sekunden von selbst oder per Klick.
